import { Button } from "antd";
import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { DISABLE_PWA_TRIGGER } from "@/config";

import { StyledWarningFilled } from "../../Styled";
import InstallWarning from "./InstallWarning";

const INTERVAL = 5 * 60 * 1000;

interface PwaInstallProps {
    isPWA: boolean;
}

export const PwaInstall: FC<PwaInstallProps> = ({ isPWA }) => {
    const [isInstallModalOpen, setIsInstallModalOpen] = useState(false);
    const [promptEvent, setPromptEvent] =
        useState<BeforeInstallPromptEvent | null>(null);

    const handleOpenModal = useCallback(() => {
        setIsInstallModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsInstallModalOpen(false);
    }, []);

    useEffect(() => {
        const onBeforeInstallPrompt = (e: Event) => {
            const bip = e as BeforeInstallPromptEvent;
            bip.preventDefault();
            setPromptEvent(bip);
        };

        const onAppInstalled = () => {
            setPromptEvent(null);
            setIsInstallModalOpen(false);
        };

        window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
        window.addEventListener("appinstalled", onAppInstalled);

        return () => {
            window.removeEventListener(
                "beforeinstallprompt",
                onBeforeInstallPrompt,
            );
            window.removeEventListener("appinstalled", onAppInstalled);
        };
    }, []);

    useEffect(() => {
        if (isPWA || DISABLE_PWA_TRIGGER) return;

        const intervalId = setInterval(() => {
            setIsInstallModalOpen(true);
        }, INTERVAL);

        return () => clearInterval(intervalId);
    }, [isPWA, promptEvent]);

    const canInstall = useMemo(() => !!promptEvent, [promptEvent]);

    const handleInstall = useCallback(async () => {
        if (!promptEvent) return;

        await promptEvent.prompt();
        const { outcome } = await promptEvent.userChoice;

        // IMPORTANT: цей event одноразовий — після prompt() його вже не треба тримати
        setPromptEvent(null);

        if (outcome === "accepted") {
            setIsInstallModalOpen(false);
        }
    }, [promptEvent]);

    if (isPWA) return null;

    return (
        <>
            <Button
                type="text"
                onClick={handleOpenModal}
                icon={<StyledWarningFilled />}
            />

            <InstallWarning
                isOpen={isInstallModalOpen}
                onClose={handleCloseModal}
                onInstall={handleInstall}
                canInstall={canInstall}
            />
        </>
    );
};
